import React, { useEffect, useRef, useState } from "react"
import { redirect, useFetcher, useLoaderData, useRouteLoaderData } from "react-router-dom"
import { Col, Container, Row } from "react-bootstrap"
import FormBuilder from "@app/components/FormBuilder"
import FormNavigationButtons from "@app/components/FormNavigationButtons"
import confirmationConfig from "@assets/confirmation-config.json"
import { useWizardSteps } from "@app/lib/hooks/useWizardSteps"
import { completeInspection } from "@app/models/inspection"
import { getJobByLocation, nextStepRedirect } from "@app/utils/inspection"
import Config from "@app/constants/Config"
import { INITIAL } from "@app/constants/Constants"
import PageWrapper from "@app/components/PageWrapper"
import { findField, updateField } from "@app/utils/form"
import { addDays, addMonths, isSameDay, startOfDay, subDays } from "date-fns"
import * as styles from "./confirmation.module.scss"
import { getDeploymentState, mapDeploymentErrorCount } from "@app/utils/confirmationState"
import { resultContentMap } from "@app/components/InspectionResultBox/InspectionResultBox"

export const action = async ({ request, params }) => {
	const { inspectionId } = params

	const formData = await request.formData()
	const data = await JSON.parse(formData.get("json"))
	const type = formData.get("type") || "INITIAL"

	// send inspection
	const action = await completeInspection(inspectionId, data, type)
	if (action?.saveAsync) {
		const res = await action.saveAsync()
		const status = res.response?.status
		if (status === 403) {
			document.cookie = `redirectTo=${window.location.pathname}; Path=/; Max-Age=600; SameSite=Lax`
			return redirect(`${Config.API.BASE_PATH}/auth?app=tempton_azure`)
		}
	}
	return nextStepRedirect("confirmation", inspectionId)
}

/*
function textList (arr = []) {
	arr = arr.map(entry => `"${entry}"`)
	if (arr.length === 1) return arr[0]
	const firsts = arr.slice(0, arr.length - 1)
	const last = arr[arr.length - 1]
	return firsts.join(", ") + " und " + last
}
*/

function getDeploymentResult (deploymentState) {
	const errorCounts = deploymentState.map(entry => mapDeploymentErrorCount(entry))
	if (errorCounts.find(({ not_possible }) => not_possible > 0)) return "not_possible"
	else if (errorCounts.find(({ possible_with_measures }) => possible_with_measures > 0)) return "possible_with_measures"
	else return "possible"
}

export default function Confirmation () {
	const { inspection, inspectionConfig } = useRouteLoaderData("inspection")
	const { userProfile, office } = useRouteLoaderData("root")
	const { currentOffice } = useLoaderData()
	const steps = useWizardSteps()
	// const [inspectionValid, setInspectionValid] = useState(true)
	const fetcher = useFetcher()
	const type = inspection.type || "INITIAL"
	const deploymentState = getDeploymentState(inspection.values, inspectionConfig.forms.steps, type)
	const deploymentResult = getDeploymentResult(deploymentState)

	const onSubmit = (data) => {
		fetcher.submit(
			{ json: JSON.stringify(data), type },
			{
				method: "post",
				encType: "application/x-www-form-urlencoded"
			}
		)
	}
	let initialValues = { ...inspection?.values } || {}

	const isFollowUp = (inspection?.type === "INITIAL" && inspection?.status === "COMPLETED") ||
		inspection?.type === "FOLLOW_UP"

	// const motherFieldConfig = confirmationConfig.fieldsets[0].elements.find(({ name }) => name === "festsetzung_geeignetfuermuetter")
	// if (motherFieldConfig) {
	// 	motherFieldConfig.initialValue = !inspection?.values?.["physischegefaehrdungsanalyse_gefaehrdungen_gefaehrdungen"]?.length > 0
	// }

	const inspectionResultFieldConfig = findField(confirmationConfig.fieldsets, "festsetzung_einsatz")
	if (inspectionResultFieldConfig) {
		inspectionResultFieldConfig.deploymentState = deploymentState
		inspectionResultFieldConfig.steps = inspectionConfig.forms.steps
		inspectionResultFieldConfig.initialValue = resultContentMap[deploymentResult]?.label
	}
	initialValues.festsetzung_einsatz = resultContentMap[deploymentResult]?.label

	const suitableForMothersErrorCount =
		deploymentState.reduce((acc, entry) => [...acc, ...Object.values(entry.not_suitable_for_mothers)], [])
			?.length || 0
	initialValues["festsetzung_geeignet-fuer-werdende-stillende-muetter"] = deploymentResult === "possible" && suitableForMothersErrorCount === 0 ? "Ja" : "Nein"

	const disponentFieldConfig = findField(confirmationConfig.fieldsets, "confirmation_name")
	if (disponentFieldConfig) {
		disponentFieldConfig.initialValue = `${userProfile?.firstName} ${userProfile?.lastName}`
	}

	const job = getJobByLocation(inspection.values, office)
	const followupOffsetMonths = job?.Turnus_Monate
		? parseInt(job.Turnus_Monate, 10)
		: office?.Turnus_6Monate
			? 6
			: Config.FOLLOWUP_TIMESPAN_DEFAULT_MONTH

	const todayStart = startOfDay(new Date())
	const minInspectionDate = subDays(todayStart, Config.CONFIRMATION_DATE_MAX_PAST_DAYS)
	const storedInspectionDate = initialValues.confirmation_date ? new Date(initialValues.confirmation_date) : null
	const initialConfirmationDate =
		storedInspectionDate && !Number.isNaN(storedInspectionDate.getTime()) ? startOfDay(storedInspectionDate) : todayStart

	const inspectionDateFieldConfig = findField(confirmationConfig.fieldsets, "confirmation_date")
	if (inspectionDateFieldConfig) {
		inspectionDateFieldConfig.initialValue = initialConfirmationDate
		inspectionDateFieldConfig.minDate = minInspectionDate
		inspectionDateFieldConfig.maxDate = todayStart
	}
	initialValues.confirmation_date = initialConfirmationDate

	const followupFieldConfig = findField(confirmationConfig.fieldsets, "followup_date")
	if (followupFieldConfig) {
		const followupDate = addMonths(startOfDay(initialConfirmationDate), followupOffsetMonths)
		followupFieldConfig.initialValue = followupDate
		followupFieldConfig.minDate = startOfDay(initialConfirmationDate)
		followupFieldConfig.maxDate = addDays(followupDate, Config.FOLLOWUP_MAX_POSTPONEMENT)
	}
	if (!initialValues.followup_date) {
		initialValues.followup_date = followupFieldConfig?.initialValue
	}
	initialValues.__followupDateHintVisible = false

	const [fieldsets, setFieldsets] = useState(confirmationConfig.fieldsets)
	const previousInspectionDateRef = useRef(initialConfirmationDate)
	const hintTimeoutRef = useRef(null)

	useEffect(() => {
		return () => clearTimeout(hintTimeoutRef.current)
	}, [])

	const handleInspectionDateChange = ({ formikProps, currentFieldsets, setCurrentFieldsets }) => {
		const inspectionDate = formikProps.values.confirmation_date
		if (!inspectionDate || isSameDay(new Date(inspectionDate), previousInspectionDateRef.current)) return
		previousInspectionDateRef.current = inspectionDate

		const followupDate = addMonths(startOfDay(inspectionDate), followupOffsetMonths)
		const followupMinDate = startOfDay(inspectionDate)
		const followupMaxDate = addDays(followupDate, Config.FOLLOWUP_MAX_POSTPONEMENT)
		const followupDateChanged = !formikProps.values.followup_date || !isSameDay(followupDate, formikProps.values.followup_date)
		formikProps.setValues({ ...formikProps.values, followup_date: followupDate }, true)
		setCurrentFieldsets(updateField(currentFieldsets, "followup_date", (element) => ({ ...element, minDate: followupMinDate, maxDate: followupMaxDate })))
		if (followupDateChanged) {
			formikProps.setFieldValue("__followupDateHintVisible", true)
			clearTimeout(hintTimeoutRef.current)
			hintTimeoutRef.current = setTimeout(() => {
				formikProps.setFieldValue("__followupDateHintVisible", false)
			}, 5000)
		}
	}

	initialValues.confirmation_office_address = currentOffice?.Adresse

	// const missingFactsheets = inspection?.values.physischegefaehrdungsanalyse_gefaehrdungen_gefaehrdungen
	// 	?.filter?.(risk => risk.physischegefaehrdungsanalyse_kategorie_kategorie === "gefahrenstoff" &&
	// 		(risk.physischegefaehrdungsanalyse_gefaehrdungstyp_gefahrenstoff_datenblattliegtvor === false || risk.physischegefaehrdungsanalyse_gefaehrdungstyp_gefahrenstoff_datenblattliegtvor === "Nein"))
	// 	.map?.(({ physischegefaehrdungsanalyse_gefaehrdungstyp_gefahrenstoff }) => physischegefaehrdungsanalyse_gefaehrdungstyp_gefahrenstoff)

	// const missingFactSheetsHint = `Vorlage Datenblatt Gefahrenstoff ${textList(missingFactsheets)} durch den Kunden.`
	// const missionImpossibleFieldConfig = findField(confirmationConfig.fieldsets, "festsetzung_einsatzistmoeglich")
	// missionImpossibleFieldConfig.initialValue = missingFactsheets?.length === 0
	// inspection.values.festsetzung_einsatzistmoeglich = missingFactsheets?.length === 0

	return (
		<PageWrapper valign={"center"} className={styles.confirmation}>
			<Container>
				<Row>
					<Col xs={12}>
						<h1 className={"mb-0"}>Festsetzung</h1>
						<p className={"text-small text-dim"}>Bitte bestätigen Sie hier abschließend die Begehung</p>
					</Col>
				</Row>
			</Container>
			<FormBuilder
				key={inspection !== null ? `${inspection.uuid}-${steps.current}` : "new-inspection"}
				values={initialValues}
				isFollowUp={isFollowUp}
				onSubmit={onSubmit}
				onChange={handleInspectionDateChange}
				fieldsets={fieldsets}
				Navigation={({ formikProps }) => (
					<FormNavigationButtons
						steps={steps}
						path={`${Config.BASE_PATH}/inspection/${inspection?.uuid}`}
						formikProps={formikProps}
						onSubmit={onSubmit}
						nextLabel={"Speichern"}
						proceedInvalid={false}
					/>
				)}
			/>
		</PageWrapper>
	)
}
